import React, { useMemo, useState, useEffect } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { PageHeader, SectionCard, StatusBadge, Mono, InfoNote } from "@/components/uikit";
import { GENESIS_HASH } from "@/lib/crypto";
import { toast } from "sonner";

function tryParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
function flatten(obj, prefix = "") {
  const out = {};
  if (obj && typeof obj === "object") {
    Object.keys(obj).forEach((k) => {
      const v = obj[k];
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object") Object.assign(out, flatten(v, key));
      else out[key] = v;
    });
  }
  return out;
}
function diffPayloads(prev, cur) {
  const a = flatten(tryParse(prev) || { payload: prev });
  const b = flatten(tryParse(cur) || { payload: cur });
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const changes = [];
  keys.forEach((k) => {
    if (String(a[k]) !== String(b[k])) changes.push({ field: k, from: a[k] ?? "—", to: b[k] ?? "—" });
  });
  return changes;
}

export default function Reconstruction() {
  const { records, integrity, stats, tamperRecord, reseed } = useApp();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (idx > records.length - 1) setIdx(Math.max(0, records.length - 1));
  }, [records.length, idx]);

  const cur = records[idx];
  const prev = records[idx - 1];
  const res = integrity[idx];
  const ok = res && res.selfOk && res.linkOk;
  const changes = useMemo(() => (prev && cur ? diffPayloads(prev.payload, cur.payload) : []), [prev, cur]);

  const curDrift = cur?.telemetry?.raw_drift ?? null;
  const prevDrift = prev?.telemetry?.raw_drift ?? null;

  if (!records.length) {
    return (
      <div>
        <PageHeader overline="Replay" title="Reconstruction & Replay" icon="History" />
        <InfoNote tone="info">No records to reconstruct. Anchor an event or reseed demo data.</InfoNote>
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Replay" title="Reconstruction & Replay" icon="History"
        subtitle="Move backward and forward through captured states. Chain breaks are located precisely."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="border-amber-800 text-amber-300 hover:bg-amber-950/40" onClick={() => { if (records[0]) { tamperRecord(records[0].id); toast.warning("Demo tamper injected into record #0"); } }} data-testid="recon-tamper-button">
              <Icons.Bug className="mr-2 h-4 w-4" /> Simulate Tamper
            </Button>
            <Button variant="outline" className="border-slate-700 text-slate-200" onClick={() => { reseed(); toast.success("Demo chain restored"); }} data-testid="recon-reseed-button">
              <Icons.RotateCcw className="mr-2 h-4 w-4" /> Reset Demo
            </Button>
          </div>
        } />

      {stats.firstBreak >= 0 && (
        <InfoNote tone="danger" className="mb-6">
          <strong>Chain continuity breaks at record #{stats.firstBreak}.</strong> Its content no longer matches its
          stored hash, or its link to the previous record is invalid. Everything after this point is unverifiable.
        </InfoNote>
      )}

      <SectionCard title="Timeline Scrubber" icon="Film" className="mb-6" testId="recon-scrubber">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="border-slate-700 text-slate-200" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} aria-label="Previous state" data-testid="recon-prev">
            <Icons.ChevronLeft className="h-4 w-4" />
          </Button>
          <input type="range" min={0} max={records.length - 1} value={idx} onChange={(e) => setIdx(Number(e.target.value))}
            className="h-2 flex-1 cursor-pointer accent-cyan-400" aria-label="Timeline position" data-testid="recon-slider" />
          <Button variant="outline" size="icon" className="border-slate-700 text-slate-200" onClick={() => setIdx((i) => Math.min(records.length - 1, i + 1))} disabled={idx === records.length - 1} aria-label="Next state" data-testid="recon-next">
            <Icons.ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {records.map((r, i) => {
            const rres = integrity[i];
            const rok = rres && rres.selfOk && rres.linkOk;
            return (
              <button key={r.id} onClick={() => setIdx(i)} data-testid={`recon-tick-${i}`}
                className={`flex h-8 w-8 items-center justify-center rounded border font-mono text-xs ${
                  i === idx ? "border-cyan-400 bg-cyan-950 text-cyan-200" : rok ? "border-slate-700 text-slate-400" : "border-rose-600 bg-rose-950/50 text-rose-300"
                }`} aria-label={`State ${i}`}>
                {i}
              </button>
            );
          })}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title={`State #${idx}`} icon="FileText" testId="recon-state-card"
          right={<StatusBadge status={ok ? "VERIFIED" : "DRIFT"} testId="recon-integrity-badge" />}>
          <StateDetails cur={cur} ok={ok} res={res} />
        </SectionCard>

        <SectionCard title={prev ? `Changes from #${idx - 1} → #${idx}` : "Genesis State"} icon="GitCompare" testId="recon-diff-card">
          <PayloadDiff prev={prev} changes={changes} curDrift={curDrift} prevDrift={prevDrift} />
        </SectionCard>
      </div>
    </div>
  );
}

function StateDetails({ cur, ok, res }) {
  return (
    <div className="space-y-2">
      <Row label="event_id" value={cur.event_id} />
      <Row label="timestamp" value={new Date(cur.timestamp).toLocaleString()} />
      <Row label="payload_type" value={cur.payload_type} />
      <Row label="drift_status" value={cur.drift_status} />
      <Row label="sha256" value={cur.sha256} />
      <Row label="previous_hash" value={cur.previous_hash === GENESIS_HASH ? "GENESIS (0×64)" : cur.previous_hash} />
      <div>
        <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">integrity result</p>
        {ok ? (
          <p className="flex items-center gap-2 text-xs text-emerald-300"><Icons.ShieldCheck className="h-4 w-4" /> Hash matches content; link to previous record valid.</p>
        ) : (
          <p className="flex items-center gap-2 text-xs text-rose-300"><Icons.ShieldAlert className="h-4 w-4" /> {res && !res.selfOk ? "Content hash mismatch (tampered)." : "Broken link to previous record."}</p>
        )}
      </div>
    </div>
  );
}

function PayloadDiff({ prev, changes, curDrift, prevDrift }) {
  if (!prev) return <InfoNote tone="info">This is the genesis record — no previous state to compare.</InfoNote>;
  return (
    <div className="space-y-3">
      {curDrift != null && prevDrift != null && (
        <div className="rounded border border-slate-800 bg-slate-950/50 p-2.5 text-xs">
          <span className="font-mono uppercase tracking-wider text-slate-500">drift delta: </span>
          <span className="font-mono text-cyan-300">{(curDrift - prevDrift).toFixed(3)}°</span>
        </div>
      )}
      {changes.length === 0 ? (
        <p className="text-sm text-slate-500">No field-level changes detected in payload.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                <th className="py-1.5 pr-3">Field</th>
                <th className="py-1.5 pr-3">Original</th>
                <th className="py-1.5">New</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.field} className="border-b border-slate-900">
                  <td className="py-1.5 pr-3 font-mono text-cyan-300">{c.field}</td>
                  <td className="py-1.5 pr-3 font-mono text-rose-300">{String(c.from)}</td>
                  <td className="py-1.5 font-mono text-emerald-300">{String(c.to)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-slate-800 bg-slate-950/60 px-3 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <Mono className="max-w-[200px] truncate sm:max-w-[300px]">{value}</Mono>
    </div>
  );
}
